/*!
 * ETAR theme v4.2.4 — Announcement bar
 * Developed by FACTICIED · https://www.facticied.com
 * Rotating messages, product spotlight, live free-delivery progress, sale countdown, schedules.
 */
(() => {
  if (customElements.get('et-announcement')) return;
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const pad = (n) => String(Math.max(0, n)).padStart(2, '0');
  const parseDate = (v) => {
    const m = String(v || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0)).getTime() : null;
  };

  class EtAnnouncement extends HTMLElement {
    connectedCallback() {
      if (this._on) return; this._on = true;
      this.editor = this.dataset.editor === 'true';
      if (this.dataset.dismiss === 'true' && !this.editor && this.closedRecently()) { this.closest('.shopify-section')?.setAttribute('hidden', ''); this.hidden = true; return; }
      this.mode = this.dataset.mode || 'rise';
      this.speed = Math.max(3, parseFloat(this.dataset.speed) || 5) * 1000;
      this.track = this.querySelector('[data-track]');
      this.dots = Array.from(this.querySelectorAll('.et-an__dots i'));
      this.ac = new AbortController();
      const o = { signal: this.ac.signal };

      this.schedule();
      if (this.mode === 'marquee') this.marquee();
      this.timers();
      this.delivery();

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.i - 1), o);
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.i + 1), o);
      this.querySelector('[data-close]')?.addEventListener('click', () => this.close(), o);
      this.querySelectorAll('[data-et-loc] select').forEach((sel) => sel.addEventListener('change', () => sel.form.submit(), o));
      if (this.dataset.pause === 'true') {
        this.addEventListener('mouseenter', () => this.hold(true), o);
        this.addEventListener('mouseleave', () => this.hold(false), o);
      }
      this.addEventListener('focusin', () => this.hold(true), o);
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.hold(false); }, o);
      if (this.dataset.swipe === 'true') this.swipe(o);
      mqMobile.addEventListener('change', () => this.refresh(), o);
      document.addEventListener('visibilitychange', () => this.hold(document.hidden), o);
      if (this.editor) {
        document.addEventListener('shopify:block:select', (e) => { const i = this.list().indexOf(e.target); if (i > -1) { this.hold(true); this.go(i); } }, o);
        document.addEventListener('shopify:block:deselect', () => this.hold(false), o);
      }
      this.refresh();
    }
    disconnectedCallback() { this.ac?.abort(); clearInterval(this.tick); clearTimeout(this.timer); }

    /* messages that can show right now on this screen */
    list() {
      const mob = mqMobile.matches;
      return Array.from(this.querySelectorAll('[data-slide]:not(.is-clone)')).filter((el) =>
        !el.classList.contains('is-off') && !el.classList.contains(mob ? 'et-an__slide--desktop' : 'et-an__slide--mobile'));
    }
    refresh() {
      this.slides = this.list();
      if (this.mode === 'marquee') return;
      this.querySelectorAll('[data-slide]').forEach((el) => el.classList.remove('is-active', 'is-out'));
      this.i = 0;
      this.show(0, true);
      this.toggleAttribute('data-single', this.slides.length < 2);
      this.loop();
    }
    show(i, first) {
      const all = this.slides; if (!all.length) return;
      const prev = all[this.cur];
      this.cur = i;
      all.forEach((el, k) => {
        const on = k === i;
        el.classList.toggle('is-active', on);
        el.classList.toggle('is-out', !on && el === prev && !first);
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
        el.querySelectorAll('a,button,select').forEach((a) => a.tabIndex = on ? 0 : -1);
      });
      const idx = Array.from(this.querySelectorAll('[data-slide]:not(.is-clone)')).indexOf(all[i]);
      this.dots.forEach((d, k) => d.classList.toggle('is-on', k === idx));
    }
    go(i) {
      const n = this.slides?.length || 0; if (n < 2) return;
      this.i = (i + n) % n; this.show(this.i); this.loop();
    }
    loop() {
      clearTimeout(this.timer);
      if (this.paused || this.mode === 'static' || (this.slides?.length || 0) < 2) return;
      this.timer = setTimeout(() => this.go(this.i + 1), this.speed);
    }
    hold(on) {
      this.paused = on; this.classList.toggle('is-paused', on);
      if (on) clearTimeout(this.timer); else this.loop();
    }
    swipe(o) {
      let x0 = null, y0 = 0;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true, ...o });
      this.addEventListener('touchend', (e) => {
        if (x0 == null) return;
        const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0; x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.i + (dx < 0 ? 1 : -1));
      }, { passive: true, ...o });
    }

    /* start / stop dates */
    schedule() {
      if (this.editor) return;
      const now = Date.now();
      this.querySelectorAll('[data-slide]').forEach((el) => {
        const a = parseDate(el.dataset.start), b = parseDate(el.dataset.end);
        if ((a && now < a) || (b && now > b)) el.classList.add('is-off');
      });
    }

    /* sale countdowns */
    timers() {
      const list = Array.from(this.querySelectorAll('[data-timer]')).map((el) => ({ el, end: parseDate(el.dataset.timer) })).filter((t) => t.end);
      if (!list.length) return;
      const run = () => {
        const now = Date.now();
        let changed = false;
        list.forEach((t) => {
          let s = Math.floor((t.end - now) / 1000);
          if (s <= 0) {
            s = 0;
            if (t.el.dataset.hide === 'true' && !this.editor) { const sl = t.el.closest('[data-slide]'); if (!sl.classList.contains('is-off')) { sl.classList.add('is-off'); changed = true; } }
          }
          t.el.hidden = false;
          t.el.querySelector('[data-d]').textContent = pad(Math.floor(s / 86400));
          t.el.querySelector('[data-h]').textContent = pad(Math.floor(s / 3600) % 24);
          t.el.querySelector('[data-m]').textContent = pad(Math.floor(s / 60) % 60);
          t.el.querySelector('[data-s]').textContent = pad(s % 60);
          t.el.classList.toggle('no-days', s < 86400);
        });
        if (changed) this.refresh();
      };
      run(); this.tick = setInterval(run, 1000);
    }

    /* free delivery: follows the bag live */
    money(cents) {
      const f = this.dataset.money || 'Rs. {{amount_no_decimals}}';
      const v = cents / 100;
      const grp = (n, d, t, s) => { const p = n.toFixed(d).split('.'); return p[0].replace(/\B(?=(\d{3})+(?!\d))/g, t) + (p[1] ? s + p[1] : ''); };
      return f.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => {
        if (k === 'amount_no_decimals') return grp(v, 0, ',', '.');
        if (k === 'amount_with_comma_separator') return grp(v, 2, '.', ',');
        if (k === 'amount_no_decimals_with_comma_separator') return grp(v, 0, '.', ',');
        if (k === 'amount_with_apostrophe_separator') return grp(v, 2, "'", '.');
        return Number.isInteger(v) ? grp(v, 0, ',', '.') : grp(v, 2, ',', '.');
      });
    }
    delivery() {
      const els = this.querySelectorAll('[data-delivery]'); if (!els.length) return;
      const sets = Array.from(els).map((el) => this.deliveryOne(el));
      const all = (cart) => sets.forEach((f) => f(cart));
      document.addEventListener('et:cart:updated', (e) => all(e.detail), { signal: this.ac.signal });
      document.addEventListener('cart:updated', (e) => all(e.detail && (e.detail.cart || e.detail)), { signal: this.ac.signal });
      /* pages served from cache can show an old bag: check once */
      if (!this.editor) fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()).then(all).catch(() => {});
    }
    deliveryOne(el) {
      const th = parseInt(el.dataset.threshold, 10) || 0;
      const text = el.querySelector('[data-dtext]'), bar = el.querySelector('[data-dbar]');
      const set = (cart) => {
        if (!cart || typeof cart.total_price !== 'number') return;
        const left = th - cart.total_price;
        const state = !cart.item_count ? 'empty' : left > 0 ? 'progress' : 'done';
        const t = el.dataset['t' + state[0].toUpperCase() + state.slice(1)] || '';
        text.textContent = t.replace('[amount]', this.money(th)).replace('[remaining]', this.money(Math.max(0, left)));
        el.dataset.state = state;
        if (bar) bar.style.transform = 'scaleX(' + (th ? Math.min(1, cart.total_price / th) : 1) + ')';
        if (state === 'done') { el.classList.remove('is-pop'); void el.offsetWidth; el.classList.add('is-pop'); }
      };
      return set;
    }

    /* continuous ribbon */
    marquee() {
      const vp = this.querySelector('[data-viewport]');
      const base = this.list(); if (!base.length) return;
      const set = document.createElement('div'); set.className = 'et-an__group';
      this.track.innerHTML = ''; this.track.appendChild(set);
      const add = (clone) => base.forEach((el) => { const c = el.cloneNode(true); c.classList.add('is-active'); c.removeAttribute('aria-hidden'); if (clone) c.classList.add('is-clone'); set.appendChild(c); });
      add(false);
      for (let k = 0; k < 8 && set.scrollWidth < vp.clientWidth + 40; k++) add(true);
      const twin = set.cloneNode(true); twin.setAttribute('aria-hidden', 'true');
      twin.querySelectorAll('[data-slide]').forEach((c) => c.classList.add('is-clone'));
      twin.querySelectorAll('a').forEach((a) => a.tabIndex = -1);
      this.track.appendChild(twin);
    }

    closedRecently() { try { const t = +localStorage.getItem('et-an-closed'); return t && Date.now() - t < (+this.dataset.dismissDays || 3) * 864e5; } catch (e) { return false; } }
    close() {
      try { localStorage.setItem('et-an-closed', String(Date.now())); } catch (e) {}
      this.classList.add('is-closing');
      setTimeout(() => { this.closest('.shopify-section')?.setAttribute('hidden', ''); this.hidden = true; }, 350);
    }
  }
  customElements.define('et-announcement', EtAnnouncement);
})();
